/**
 * Logistics reports.
 *
 * One endpoint behind five periods and five filters. The server does the
 * aggregating — grouping a growing ledger in the browser would work until the
 * day it did not, and that day arrives without warning.
 *
 * Remittance reads as not applicable at day grain rather than as zero. It is
 * agreed per period, and a seventh of a weekly figure is a number nobody ever
 * agreed to.
 */

import { useMemo, useState, type ReactNode } from 'react';
import {
  Card, EmptyState, ErrorState, Input, Select, TableSkeleton,
} from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { money } from '../../lib/format';
import { SectionTitle, Stat } from './parts';
import { useLogisticsReport, useRiders } from './data';
import { LocationScope } from '../../components/LocationScope';
import { useLocationFilter, useLocationReady, useWorkingLocation } from '../../lib/locationContext';

type Period = 'today' | 'week' | 'month' | 'year' | 'custom';

const PERIODS: Array<{ value: Period; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
  { value: 'custom', label: 'Custom range' },
];

/** Sunday-based weeks, as CenoHub's own books have always run. */
function windowFor(period: Exclude<Period, 'custom'>, timezone: string): { from: string; to: string } {
  const now = new Date(new Date().toLocaleString('en-US', { timeZone: timezone }));
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const day = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  if (period === 'today') return { from: iso(day), to: iso(day) };
  if (period === 'week') {
    const start = new Date(day);
    start.setUTCDate(start.getUTCDate() - start.getUTCDay());
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + 6);
    return { from: iso(start), to: iso(end) };
  }
  if (period === 'month') {
    return {
      from: iso(new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1))),
      to: iso(new Date(Date.UTC(now.getFullYear(), now.getMonth() + 1, 0))),
    };
  }
  return {
    from: iso(new Date(Date.UTC(now.getFullYear(), 0, 1))),
    to: iso(new Date(Date.UTC(now.getFullYear(), 11, 31))),
  };
}

export function LogisticsReportsPage(): ReactNode {
  const { business } = useAuth();
  const currency = business?.currencyCode ?? 'NGN';
  const timezone = business?.timezone ?? 'Africa/Lagos';

  const [period, setPeriod] = useState<Period>('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [dimension, setDimension] = useState<'rider' | 'location' | 'status' | 'source' | 'origin'>('rider');
  const [origin, setOrigin] = useState('');
  const [riderFilter, setRiderFilter] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const scope = useLocationFilter();
  const { scopedLocations } = useWorkingLocation();

  const range = useMemo(() => {
    if (period === 'custom') {
      const today = windowFor('today', timezone);
      return { from: customFrom || today.from, to: customTo || today.to };
    }
    return windowFor(period, timezone);
  }, [period, customFrom, customTo, timezone]);

  const grain =
    period === 'today' ? 'day' : period === 'week' ? 'week' : period === 'month' ? 'month' : period === 'year' ? 'year' : 'day';

  const report = useLogisticsReport({
    ...range,
    grain,
    dimension,
    origin: origin === '' ? undefined : (origin as 'live' | 'import'),
    // The sidebar's Country → State/Region → Location.
    ...scope,
  }, useLocationReady());
  const riders = useRiders();

  if (report.isError) {
    return <ErrorState message="Could not load the report." onRetry={() => void report.refetch()} />;
  }

  const t = report.data?.totals;
  /*
    Whether remittance applies is a question about the RANGE, not the grain.

    The server only ever sums periods that fall wholly inside the range
    (`p.starts_on >= from and p.ends_on <= to`), so `totals.remittance` is
    always a whole-period figure and never a fraction of one. Keying the stat
    off `grain === 'day'` instead hid a correct ₦495,000 on a custom range
    covering four complete weeks — the range the June–July history reconciles
    on. A range containing no whole period still reads "—", which is the case
    the original rule was really protecting.

    The per-row suppression below stays keyed on grain: a single day genuinely
    has no allocation, and a seventh of a weekly figure is a number nobody
    agreed to.
  */
  const dayGrain = grain === 'day';
  const hasWholePeriod = (report.data?.periods.length ?? 0) > 0;

  /*
    Rider and branch narrow the rows already returned rather than re-querying.
    Both dimensions are small — a courier has a dozen riders and three branches
    — so this is a filter on a short list, not a way of avoiding pagination.
    Location and the unbounded dimensions are served and capped by the API.
  */
  const visibleDimension = (report.data?.byDimension ?? []).filter((row) => {
    if (dimension === 'rider' && riderFilter) return row.label === riderFilter;
    if (dimension === 'location' && branchFilter) return row.label === branchFilter.toUpperCase();
    return true;
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-2.5">
        <Select
          label="Period"
          value={period}
          onChange={(e) => setPeriod(e.target.value as Period)}
          options={PERIODS.map((p) => ({ value: p.value, label: p.label }))}
        />
        {period === 'custom' ? (
          <>
            <Input
              label="From"
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
            />
            <Input
              label="To"
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
            />
          </>
        ) : null}
        <Select
          label="Break down by"
          value={dimension}
          onChange={(e) => setDimension(e.target.value as typeof dimension)}
          options={[
            { value: 'rider', label: 'Rider' },
            { value: 'location', label: 'Location' },
            { value: 'status', label: 'Status' },
            { value: 'source', label: 'Source channel' },
            { value: 'origin', label: 'Live or imported' },
          ]}
        />
        <Select
          label="Origin"
          value={origin}
          onChange={(e) => setOrigin(e.target.value)}
          options={[
            { value: '', label: 'Live and imported' },
            { value: 'live', label: 'Live only' },
            { value: 'import', label: 'Imported only' },
          ]}
        />
        {dimension === 'rider' ? (
          <Select
            label="Rider"
            value={riderFilter}
            onChange={(e) => setRiderFilter(e.target.value)}
            options={[
              { value: '', label: 'All riders' },
              ...(riders.data?.data ?? []).map((r) => ({
                value: r.canonicalName,
                label: r.canonicalName,
              })),
            ]}
          />
        ) : null}
        {dimension === 'location' ? (
          <Select
            label="Branch"
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
            options={[
              { value: '', label: 'All locations' },
              // Only the locations inside the sidebar's scope have rows here.
              ...scopedLocations.map((b) => ({ value: b.name, label: b.name })),
            ]}
          />
        ) : null}
      </div>

      <LocationScope testId="logistics-report-scope" />
      <p className="text-xs text-slate-400">
        {range.from === range.to ? range.from : `${range.from} – ${range.to}`}
      </p>

      {report.isPending ? (
        <TableSkeleton />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label="Deliveries" count={t?.deliveries ?? 0} />
            <Stat label="Gross" value={t?.gross} currency={currency} />
            <Stat label="Rider payout" value={t?.riderPayout} currency={currency} />
            <Stat label="Company share" value={t?.companyShare} currency={currency} tone="good" />
            <Stat
              label="Remittance"
              value={hasWholePeriod ? t?.remittance : null}
              currency={currency}
              hint={hasWholePeriod ? undefined : 'per period'}
            />
            <Stat
              label="Total revenue"
              value={hasWholePeriod ? t?.totalRevenue : null}
              currency={currency}
              tone="good"
              hint="commission + remittance"
            />
          </div>

          <section>
            <SectionTitle>Over time</SectionTitle>
            {(report.data?.byGrain ?? []).length === 0 ? (
              <EmptyState title="No deliveries in this range" />
            ) : (
              <Card>
                <div className="-mx-3 overflow-x-auto px-3">
                  <table className="w-full min-w-[560px] text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                        <th className="py-2 pr-3 font-medium">Period</th>
                        <th className="py-2 pr-3 text-right font-medium">Deliveries</th>
                        <th className="py-2 pr-3 text-right font-medium">Earning</th>
                        <th className="py-2 pr-3 text-right font-medium">Gross</th>
                        <th className="py-2 pr-3 text-right font-medium">Payout</th>
                        <th className="py-2 text-right font-medium">Company</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {report.data!.byGrain.map((row) => (
                        <tr key={row.key}>
                          <td className="py-2 pr-3 text-slate-700">{row.label}</td>
                          <td className="py-2 pr-3 text-right tabular-nums text-slate-600">
                            {row.deliveries}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums text-slate-500">
                            {row.financiallyComplete}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums text-slate-900">
                            {money(row.gross, currency)}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums text-slate-700">
                            {money(row.riderPayout, currency)}
                          </td>
                          <td className="py-2 text-right tabular-nums font-medium text-emerald-700">
                            {money(row.companyShare, currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </section>

          <section>
            <SectionTitle
              aside={
                report.data?.dimensionTruncated ? (
                  <span className="text-xs text-amber-700">
                    Showing the top 200 — narrow the range to see the rest
                  </span>
                ) : undefined
              }
            >
              By {dimension}
            </SectionTitle>
            {visibleDimension.length === 0 ? (
              <EmptyState title="Nothing to break down here" />
            ) : (
              <Card>
                <div className="-mx-3 overflow-x-auto px-3">
                  <table className="w-full min-w-[520px] text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                        <th className="py-2 pr-3 font-medium">{dimension}</th>
                        <th className="py-2 pr-3 text-right font-medium">Deliveries</th>
                        <th className="py-2 pr-3 text-right font-medium">Gross</th>
                        <th className="py-2 pr-3 text-right font-medium">Payout</th>
                        <th className="py-2 text-right font-medium">Company</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {visibleDimension.map((row) => (
                        <tr key={row.key}>
                          <td className="py-2 pr-3 text-slate-700">
                            {row.label}
                            {row.key.startsWith('unresolved:') ? (
                              <span className="ml-1.5 text-xs text-amber-700">unmapped</span>
                            ) : null}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums text-slate-600">
                            {row.deliveries}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums text-slate-900">
                            {money(row.gross, currency)}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums text-slate-700">
                            {money(row.riderPayout, currency)}
                          </td>
                          <td className="py-2 text-right tabular-nums font-medium text-emerald-700">
                            {money(row.companyShare, currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </section>
        </>
      )}
    </div>
  );
}
